import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { OrdersApiService } from '../../../services/orders-api.service';
import { Order, OrderAssignmentLog } from '../../../models/order';
import { CommonModule } from '@angular/common';
import { GoogleMapComponent } from '../../../../../shared/map/google-map/google-map.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../../shared/services/translations/language.service';
import { DriversManagementApiService } from '../../../../drivers-management/services/drivers-management-api.service';
import { Driver } from '../../../../drivers-management/models/driver';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { ToastNotificationType, ToastPosition } from '../../../../../notifications/models/toast-notification-type';
import { ApiErrorService } from '../../../../../shared/services/api-error.service';

@Component({
  selector: 'app-delivery-order-details',
  imports: [CommonModule, FormsModule, RouterLink, GoogleMapComponent, TranslateModule],
  templateUrl: './delivery-order-details.component.html',
  styleUrl: './delivery-order-details.component.css'
})
export class DeliveryOrderDetailsComponent implements OnInit {

  route = inject(ActivatedRoute);
  apiService = inject(OrdersApiService);
  apiErrorService = inject(ApiErrorService);
  driversApiService = inject(DriversManagementApiService);
  toast = inject(ToastNotificationService);
  translate = inject(TranslateService);
  languageService = inject(LanguageService);

  orderUUID = this.route.snapshot.paramMap.get('id');

  order: Order | undefined;
  assignmentHistory: OrderAssignmentLog[] = [];

  drivers: Driver[] = [];
  showDriverPicker = false;
  selectedDriverId: number | null = null;
  assigning = false;

  private readonly finalizedStatuses = ['delivered', 'cancelled', 'refunded'];

  get totalOrderPrice(): number {
    if (!this.order) return 0;
    return this.order.items.reduce((total, item) => total + item.price * item.quantity, 0);
  }

  get isFinalized(): boolean {
    return !!this.order && this.finalizedStatuses.includes(this.order.status);
  }

  get isCancelledOrRefunded(): boolean {
    return !!this.order && (this.order.status === 'cancelled' || this.order.status === 'refunded');
  }

  ngOnInit(): void {
    if (!this.orderUUID) {
      this.toast.createNotification(this.translate.instant('orders.errors.uuid_missing'), ToastNotificationType.ERROR);
      return;
    }

    this.apiService.getOrderByUUID(this.orderUUID).subscribe({
      next: order => { this.order = order; },
      error: () => {
        this.toast.createNotification(this.translate.instant('orders.errors.fetch_failed'), ToastNotificationType.ERROR);
      }
    });

    this.loadDrivers();
    this.loadAssignmentHistory();
  }

  private loadDrivers(): void {
    this.driversApiService.getDrivers({ limit: 50, offset: 0 }).subscribe({
      next: res => { this.drivers = res.results; },
      error: () => { /* picker just stays empty */ }
    });
  }

  private loadAssignmentHistory(): void {
    if (!this.orderUUID) return;
    this.apiService.getOrderAssignmentHistory(this.orderUUID).subscribe({
      next: rows => { this.assignmentHistory = rows; },
      error: () => { /* timeline optional */ }
    });
  }

  // --- Driver assignment ---

  openPicker(): void {
    this.selectedDriverId = this.order?.assigned_driver?.id ?? null;
    this.showDriverPicker = true;
  }

  cancelPicker(): void {
    this.showDriverPicker = false;
  }

  confirmAssign(): void {
    if (this.selectedDriverId == null) return;
    this.assignDriver(this.selectedDriverId);
  }

  unassign(): void {
    this.assignDriver(null);
  }

  private assignDriver(driverId: number | null): void {
    if (!this.orderUUID || this.assigning) return;
    this.assigning = true;
    this.apiService.assignDriver(this.orderUUID, driverId).subscribe({
      next: order => {
        this.order = order;
        this.showDriverPicker = false;
        this.assigning = false;
        this.loadAssignmentHistory();
        const msg = driverId == null
          ? 'orders.details.driver.unassigned_toast'
          : 'orders.details.driver.assigned_toast';
        this.toast.createNotification(this.translate.instant(msg), ToastNotificationType.SUCCESS, '', ToastPosition.BOTTOM_RIGHT);
      },
      error: (err) => {
        this.assigning = false;
        this.toast.createNotification(this.apiErrorService.getDisplayMessage(err, this.translate.instant('orders.details.driver.assign_failed')), ToastNotificationType.ERROR, '', ToastPosition.TOP_CENTER);
      }
    });
  }

  // --- Multi-point grouping ---

  itemsForVendor(vendorId: number) {
    return this.order?.items.filter(item => item.vendor_id === vendorId) ?? [];
  }

  // --- Contact / map helpers ---

  mapsLink(lat?: number | null, lng?: number | null): string | null {
    if (lat == null || lng == null) return null;
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }

  telLink(phone?: string | null): string | null {
    return phone ? `tel:${phone}` : null;
  }

  expectedDelivery(): string | null {
    return this.order?.estimated_ship_datetime || this.order?.scheduled_for || null;
  }
}
